// InheritableField.tsx — Buildings Set1 §8 shared control: the workhorse for every nullable field
// in the building modifier system.
//
// A nullable field participates in the §3 fallback chain. This control shows:
//   - the RESOLVED value (from `ResolveBuildingDefaults`), greyed when the local field is null;
//   - the provenance label ("inherited from Level: Floor 3" / "Building prototype" / ...);
//   - an explicit "override" / "reset to inherited" toggle.
//
// Editing while the field is null WRITES an override (un-nulls it); "reset to inherited" nulls it
// back. This mirrors the scenery/material "value vs fallback" UI.
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, Row, RowLR, Spinner, Text, TextInput} from "react-vcomponents";
import {TextPlus} from "web-vcore";

export type InheritableFieldProps = {
	/** Human label shown to the left. */
	label: string;
	info?: string;
	/** Resolved value from the §3 merge — displayed (greyed) when `value` is null. */
	resolvedValue: unknown;
	/** Local (overridden) value; null/undefined = inheriting. */
	value: unknown;
	/** Called with a NON-null new value to write an override; or with null to reset to inherited. */
	onChange: (newValue: unknown)=>void;
	/** Provenance of the resolved value (e.g. "Level (Floor 3)"); shown when inheriting. */
	provenance: string;
	/** Number spinner config. When set, renders a numeric spinner; else a toggle for booleans. */
	number?: {min?: number; max?: number; step?: number; allowFraction?: boolean};
	/** For string/recipe-ish pickers, allow a free text input (minimal; full recipe picker is
	 *  MaterialSlotPicker's job). */
	text?: boolean;
};

export const InheritableField = observer_mgl((props: InheritableFieldProps)=>{
	const {label, info, resolvedValue, value, onChange, provenance, number, text} = props;
	const isOverridden = value != null;
	const shown: unknown = isOverridden ? value : resolvedValue;

	const isNumber = number != null;
	const isBool = !isNumber && !text && typeof shown == "boolean";

	return (
		<RowLR mt={5} splitAt={180}>
			<Row style={{alignItems: "center", gap: 4}}>
				<TextPlus info={info} style={{color: "#ccc"}}>{label}</TextPlus>
				{!isOverridden && provenance &&
					<Text style={{color: "#777", fontSize: 11}}>(from {provenance})</Text>}
			</Row>
			<Row style={{alignItems: "center", gap: 4}}>
				{isNumber &&
					<Spinner
						min={number.min} max={number.max} step={number.step ?? 0.1}
						placeholder={""+resolvedValue}
						value={value as number|n}
						onChange2={(val)=>{
							// onChange2 fires with null on clear; treat null as reset-to-inherit.
							if (val == null) onChange(null);
							else onChange(clampNumber(val, number));
						}}/>}
				{isBool &&
					<Button text={shown ? "On" : "Off"}
						style={{backgroundColor: shown ? "rgba(100,200,100,.4)" : "rgba(200,100,100,.4)"}}
						onClick={()=>onChange(!shown)}/>}
				{!isNumber && !isBool && text &&
					<TextInput style={{width: 90, padding: "2px 4px"}}
						placeholder={""+resolvedValue}
						value={value != null ? ""+value : ""} onChange={val=>onChange(val || null)}/>}
				<Button text={isOverridden ? "↺" : "▾"}
					title={isOverridden ? "Reset to inherited (clears this override)" : "Override this field"}
					onClick={()=>onChange(isOverridden ? null : (resolvedValue ?? number?.min ?? 0))}/>
			</Row>
		</RowLR>
	);
});

function clampNumber(val: number, cfg: {min?: number; max?: number}): number {
	if (cfg.min != null && val < cfg.min) return cfg.min;
	if (cfg.max != null && val > cfg.max) return cfg.max;
	return val;
}
